import React from 'react'
import PropTypes from 'prop-types'
import { Checkbox, Popover } from '@material-ui/core'
import { ACTIVITY_TYPES } from './presets'

export const ALL_ACTIVITY_TYPE_VALUES = Object.values(ACTIVITY_TYPES)

export const chipStyle = {
  display: 'inline-block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-color-subtle)',
  background: 'var(--color-neutral-100)',
  borderRadius: '4px',
  padding: '1px 6px',
  marginRight: '4px'
}

export const editChipStyle = {
  ...chipStyle,
  background: 'transparent',
  color: 'var(--color-primary-500)',
  fontWeight: 600,
  cursor: 'pointer'
}

export const badgeColors = {
  valuation: { background: 'var(--color-primary-100)', color: 'var(--color-primary-600)' },
  derived: { background: 'var(--color-accent-100)', color: 'var(--color-accent-600)' },
  performance: { background: 'var(--color-success-100)', color: 'var(--color-success-600)' }
}

const popoverHeadingStyle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  padding: '0 8px 4px',
  borderBottom: '1px solid var(--color-neutral-100)',
  marginBottom: '4px'
}

export function ClickableChipArea ({ children, onClick, title }) {
  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e) } }}
      style={{
        marginTop: '2px',
        cursor: 'pointer',
        borderRadius: '4px',
        padding: '2px 0',
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: '2px'
      }}
      title={title}
    >
      {children}
      <span style={editChipStyle}>&#x270E;</span>
    </div>
  )
}

ClickableChipArea.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string
}

export function CheckboxPopover ({ anchorEl, open, onClose, heading, items, selectedKeys, onToggle, minSelected }) {
  const min = minSelected ?? 0

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <div style={{ padding: '8px 4px', maxHeight: '300px', overflowY: 'auto', minWidth: '240px' }}>
        <div style={popoverHeadingStyle}>{heading}</div>
        {items.map(({ key, label }) => {
          const checked = selectedKeys.has(key)
          const disabled = checked && selectedKeys.size <= min
          return (
            <div
              key={key}
              role='button'
              tabIndex={0}
              onClick={() => !disabled && onToggle(key)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!disabled) onToggle(key) } }}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '2px 4px',
                cursor: disabled ? 'default' : 'pointer',
                borderRadius: '4px',
                opacity: disabled ? 0.5 : 1
              }}
            >
              <Checkbox
                checked={checked}
                disabled={disabled}
                size='small'
                color='primary'
                style={{ padding: '2px' }}
                tabIndex={-1}
              />
              <span style={{ fontSize: 'var(--font-size-sm)', marginLeft: '4px' }}>{label}</span>
            </div>
          )
        })}
      </div>
    </Popover>
  )
}

CheckboxPopover.propTypes = {
  anchorEl: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  heading: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  selectedKeys: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
  minSelected: PropTypes.number
}

export function RowTypeDetail ({ row, activityRows, onChipAreaClick }) {
  if (row.rowType === 'activity') {
    return (
      <ClickableChipArea onClick={onChipAreaClick} title='Click to edit activity types'>
        {(row.activityTypes || []).map(t => (
          <span key={t} style={chipStyle}>{t}</span>
        ))}
      </ClickableChipArea>
    )
  }

  if (row.rowType === 'derived' && row.derivation === 'mvChange' && activityRows?.length) {
    const included = row.includedActivityKeys
    const isFiltered = Array.isArray(included)
    const includedSet = isFiltered ? new Set(included) : null
    const shownRows = isFiltered
      ? activityRows.filter(r => includedSet.has(r.key))
      : activityRows

    return (
      <ClickableChipArea onClick={onChipAreaClick} title='Click to edit which activity rows are included'>
        <span style={{ ...chipStyle, ...badgeColors.derived, fontWeight: 600 }}>Computed</span>
        {shownRows.map(r => (
          <span key={r.key} style={chipStyle}>{r.name}</span>
        ))}
        {!isFiltered && (
          <span style={{ ...chipStyle, fontStyle: 'italic' }}>all activity</span>
        )}
      </ClickableChipArea>
    )
  }

  const badge = badgeColors[row.rowType] || badgeColors.derived
  let label = row.rowType.charAt(0).toUpperCase() + row.rowType.slice(1)
  if (row.rowType === 'performance') label = row.method
  if (row.rowType === 'derived') label = 'Computed'

  return (
    <div style={{ marginTop: '2px' }}>
      <span style={{
        ...chipStyle,
        background: badge.background,
        color: badge.color,
        fontWeight: 600
      }}
      >
        {label}
      </span>
    </div>
  )
}

RowTypeDetail.propTypes = {
  row: PropTypes.object.isRequired,
  activityRows: PropTypes.array,
  onChipAreaClick: PropTypes.func
}
